import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";

import { getCustomSelectStyles } from "@/modules/common";
import useStore from "@/modules/store";
import useDarkMode from "@/hooks/useDarkMode";
import {
  CACHE_INVALIDATED_EVENT,
  fetchWithCache,
  shouldRefreshForInvalidation,
} from "@/modules/api_cache";
import { reportPerformanceMetric, startMetricTimer } from "@/modules/performance_metrics";

const GenericMultiSelect = ({ id, onChange, required, apiPath, placeholder }) => {
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const selects = useStore((state) => state.selects);
  const isInvalid = useStore((state) => state.selects[id]?.invalid);
  const setSelectValue = useStore((state) => state.setSelectValue);
  const isDark = useDarkMode();
  const apiResource = apiPath.split("/")[0];

  const fetchOptions = useCallback(async () => {
    const stopTimer = startMetricTimer(
      "select_options_ready_ms",
      { api_resource: apiResource, component: "multi_select" },
      "interaction",
    );

    try {
      const data = await fetchWithCache(apiPath);
      const formattedOptions = data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setOptions(formattedOptions);
      stopTimer({ option_count: formattedOptions.length });
    } catch (error) {
      reportPerformanceMetric(
        "select_options_error",
        1,
        { api_resource: apiResource, component: "multi_select", error: "fetch_failed" },
        "interaction",
        "counter",
      );
      console.error("Error fetching options:", error);
      setOptions([]);
    }
  }, [apiPath, apiResource]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    const handleCacheInvalidation = (event) => {
      if (shouldRefreshForInvalidation(apiPath, event.detail?.apiPath)) {
        fetchOptions();
      }
    };

    window.addEventListener(CACHE_INVALIDATED_EVENT, handleCacheInvalidation);
    return () => {
      window.removeEventListener(CACHE_INVALIDATED_EVENT, handleCacheInvalidation);
    };
  }, [apiPath, fetchOptions]);

  useEffect(() => {
    const currentSelectValue = selects[id]?.value;
    if (
      currentSelectValue &&
      Array.isArray(currentSelectValue) &&
      currentSelectValue.length > 0
    ) {
      const selected = currentSelectValue
        .map((val) => options.find((option) => option.value === Number(val)))
        .filter(Boolean);
      setSelectedOptions(selected);
    } else {
      setSelectedOptions([]);
    }
  }, [selects[id]?.value, id, options]);

  const handleChange = (nextSelectedOptions) => {
    const normalizedOptions = nextSelectedOptions || [];
    setSelectedOptions(normalizedOptions);
    if (onChange) {
      onChange(normalizedOptions.map((option) => option.value).join(","));
    }
    setSelectValue(
      id,
      normalizedOptions.length
        ? normalizedOptions.map((option) => option.value)
        : [],
      required,
      isInvalid && !normalizedOptions.length,
    );
  };

  const customStyles = getCustomSelectStyles(required, isInvalid, isDark);

  const displayPlaceholder = placeholder || "请选择...";

  return (
    <Select
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      styles={customStyles}
      isMulti={true}
      className="span-sm-gray"
      placeholder={displayPlaceholder}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      maxMenuHeight={240}
    />
  );
};

export default GenericMultiSelect;
